'use client';
import {
  Box,
  IconButton,
  Input,
  Stack,
  Text,
  Link,
  VStack,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { useEffect, useState, useMemo } from 'react';
import {
  DialogRoot,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogTrigger,
  DialogBackdrop,
  DialogCloseTrigger,
  DialogContent,
} from '../ui/dialog';
import { AiTwotoneCloseCircle } from 'react-icons/ai';
import { useTranslations } from 'next-intl';
import {
  requestEmailRegister,
  requestGetEmailCode,
} from '@/lib/services/user.service';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '../ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { MD5 } from '@/lib/utils/crypto';
import * as z from 'zod';
import { toaster } from '@/components/ui/toaster';
import { BiSolidHide, BiSolidShow } from 'react-icons/bi';
import { useAppDispatch, useAppSelector } from '@/lib/hooks/storeHook';
import {
  selectEmailRegisterOpen,
  setEmailRegisterOpen,
  setEmailLoginOpen,
} from '@/lib/stores/uiSlice';

const MIN_PASSWORD_LENGTH = 8;

type Props = {
  onLogin: () => void;
};

export function EmailRegister({ onLogin }: Props): JSX.Element {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const t = useTranslations('Navigation.emailRegister');
  const tAuth = useTranslations('Auth');
  const dispatch = useAppDispatch();
  const emailRegisterOpen = useAppSelector(selectEmailRegisterOpen);

  const [countdown, setCountdown] = useState(0);

  const signUpSchema = useMemo(
    () =>
      z
        .object({
          email: z.string().email(tAuth('validation.emailInvalid')),
          password: z
            .string()
            .min(MIN_PASSWORD_LENGTH, tAuth('validation.passwordLength'))
            .regex(/\d/, tAuth('validation.passwordNumber'))
            .regex(/[A-Z]/, tAuth('validation.passwordUppercase')),
          confirmPassword: z.string(),
          verificationCode: z
            .string()
            .min(1, tAuth('validation.verificationCodeRequired')),
          inviteCode: z.string().optional(),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: tAuth('validation.passwordsNotMatch'),
          path: ['confirmPassword'],
        }),
    [tAuth],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    getValues,
  } = useForm<z.infer<typeof signUpSchema>>({
    resolver: zodResolver(signUpSchema as any),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      verificationCode: '',
      inviteCode: '',
    },
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [countdown]);

  const getEmailVerificationCode = async () => {
    const res = await requestGetEmailCode(getValues('email'));
    if (res) {
      setCountdown(60);
    } else {
      toaster.create({
        title: tAuth('networkError'),
        description: tAuth('networkError'),
        type: 'error',
        duration: 3000,
      });
      setCountdown(0);
    }
  };

  const handleRegister = async (data: z.infer<typeof signUpSchema>) => {
    if (!agreed) return;
    setLoading(true);
    try {
      await requestEmailRegister(
        data.email,
        MD5(data.password),
        data.verificationCode,
        data.inviteCode,
      );
      onLogin();
    } catch (error: any) {
      toaster.create({
        title: tAuth('registerFailed'),
        description: error.message,
        type: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = handleSubmit(handleRegister);

  return (
    <DialogRoot
      placement={'center'}
      open={emailRegisterOpen}
      onOpenChange={(e) => dispatch(setEmailRegisterOpen(e.open))}
    >
      <DialogBackdrop />
      <DialogTrigger asChild>
        <Button
          px={6}
          py={2}
          h="auto"
          borderRadius="100px"
          variant="outline"
          size="sm"
          borderColor="text.t1"
          color="text.t1"
          _hover={{
            opacity: 0.8,
          }}
        >
          {t('signup')}
        </Button>
      </DialogTrigger>

      <DialogContent maxW="480px" width="100%" borderRadius="20px">
        <DialogHeader>
          <DialogTitle color="text.t1" pt={5} pb={3} px={5}>
            {t('registerTitle')}
          </DialogTitle>
          <DialogCloseTrigger>
            <Icon fontSize="24px">
              <AiTwotoneCloseCircle />
            </Icon>
          </DialogCloseTrigger>
        </DialogHeader>

        <DialogBody>
          <Stack
            width="480px"
            bg="bg.t1"
            p={8}
            borderRadius="xl"
            position="relative"
            gap={4}
          >
            {/* Email Input */}
            <VStack w="full" align="start" gap={1}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t('email')}
                    size="lg"
                    bg="transparent"
                    border="1px solid"
                    borderColor="text.t4"
                    rounded={8}
                    variant="outline"
                    py={'6px'}
                    px={3}
                  />
                )}
              />
              {errors.email && (
                <Text fontSize="11px" color="red.500" alignSelf="start">
                  {errors.email.message}
                </Text>
              )}
            </VStack>

            {/* Password Input with Toggle Icon */}
            <VStack w="full" align="start" gap={1} position="relative">
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('password')}
                    size="lg"
                    border="1px solid"
                    borderColor="text.t4"
                    rounded={8}
                    py={'6px'}
                    px={3}
                  />
                )}
              />
              {errors.password ? (
                <Text fontSize="11px" color="red.500" alignSelf="start">
                  {errors.password.message}
                </Text>
              ) : (
                <Text fontSize="11px" color="text.t3" alignSelf="start">
                  {t('passwordRules')}
                </Text>
              )}
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                variant="ghost"
                position="absolute"
                right={2}
                top={1}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <BiSolidHide /> : <BiSolidShow />}
              </IconButton>
            </VStack>

            {/* Confirm Password Input with Toggle Icon */}
            <VStack w="full" align="start" gap={1} position="relative">
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={t('confirmPassword')}
                    size="lg"
                    border="1px solid"
                    borderColor="text.t4"
                    rounded={8}
                    py={'6px'}
                    px={3}
                  />
                )}
              />
              {errors.confirmPassword && (
                <Text fontSize="11px" color="red.500" alignSelf="start">
                  {errors.confirmPassword.message}
                </Text>
              )}
              <IconButton
                aria-label={
                  showConfirmPassword ? 'Hide password' : 'Show password'
                }
                variant="ghost"
                position="absolute"
                right={2}
                top={1}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <BiSolidHide /> : <BiSolidShow />}
              </IconButton>
            </VStack>

            <Box position="relative" w="full">
              <VStack w="full" gap={1} align="start">
                <Controller
                  name="verificationCode"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={t('verificationCode')}
                      size="lg"
                      border="1px solid"
                      borderColor="text.t4"
                      rounded={8}
                      pr="100px"
                      py={'6px'}
                      px={3}
                    />
                  )}
                />
                {errors.verificationCode && (
                  <Text fontSize="11px" color="red.500" alignSelf="start">
                    {errors.verificationCode.message}
                  </Text>
                )}
              </VStack>
              <Button
                position="absolute"
                right={2}
                top={1}
                size="sm"
                variant="ghost"
                color="text.primary"
                disabled={countdown > 0}
                onClick={getEmailVerificationCode}
              >
                {countdown > 0 ? `${countdown}s` : t('getCode')}
              </Button>
            </Box>

            {/* Invitation Code Input */}
            <Controller
              name="inviteCode"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('invitationCode')}
                  size="lg"
                  border="1px solid"
                  borderColor="text.t4"
                  rounded={8}
                  py={'6px'}
                  px={3}
                />
              )}
            />

            {/* Terms and Conditions */}
            <HStack>
              <Checkbox
                checked={agreed}
                onCheckedChange={(e) => setAgreed(!!e.checked)}
              />
              <Text fontSize="sm" color="text.t2">
                {t('clickToAgree')}
                <Link
                  color="text.primary"
                  href={t('termsOfServiceUrl')}
                  target="_blank"
                >
                  {t('termsOfService')}
                </Link>
                {t('and')}
                <Link
                  color="text.primary"
                  href={t('privacyUrl')}
                  target="_blank"
                >
                  {t('privacy')}
                </Link>
              </Text>
            </HStack>
          </Stack>
        </DialogBody>

        <DialogFooter bg="bg.t1" borderRadius="20px" overflow="hidden">
          {/* Submit Button */}
          <VStack width="full" pb={5} bg="bg.t1">
            <Button
              size="lg"
              bg="text.t1"
              color="bg.t1"
              borderRadius="99px"
              width="440px"
              disabled={!agreed}
              _hover={{
                opacity: 0.8,
              }}
              onClick={onSubmit}
              loading={loading}
            >
              {t('signup')}
            </Button>

            {/* Resend Link */}
            <HStack px={5} width="full" justify="space-between">
              <Link
                color="text.primary"
                textAlign="center"
                fontSize="sm"
                href="https://t.me/pumpxai"
                target="_blank"
                _focus={{
                  outline: 'none',
                }}
              >
                {t('needHelp')}
              </Link>
              <Text
                cursor="pointer"
                color="text.primary"
                textAlign="center"
                fontSize="sm"
                onClick={() => {
                  dispatch(setEmailRegisterOpen(false));
                  dispatch(setEmailLoginOpen(true));
                }}
              >
                {t('login')}
              </Text>
            </HStack>
          </VStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
